// Reads an answer's setup parts (v3 `orpheusSetupParts.ts`). The same parts arrive from three
// places, so every reader goes through `extractSetupParts`:
//   - the main chat's live answer, under `response.setup` / `response.card` / `response.file_preview`
//   - the main chat's history, under the stored message's `metadata`
//   - the feature-chat panel's answer and history payload, at the top level
import type {
  FilePreview,
  FilePreviewCell,
  SetupCard,
  SetupOption,
  SetupParts,
  SetupSlotSource,
  SetupStep,
} from '../models/orpheusSetup.types'

const SLOT_SOURCES: readonly SetupSlotSource[] = ['user', 'system', 'missing']

type Raw = Record<string, unknown>

function isObject(value: unknown): value is Raw {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function text(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  return typeof value === 'number' ? String(value) : ''
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

export function normalizeSetupStep(raw: unknown): SetupStep | null {
  if (!isObject(raw)) return null
  const askingField = text(raw.asking_field)
  if (!askingField) return null

  const options = (Array.isArray(raw.options) ? raw.options : [])
    .filter(isObject)
    .map((option) => {
      const value = text(option.value)
      return { label: text(option.label) || value, description: text(option.description), value }
    })
    .filter((option) => option.value)

  const slots = (Array.isArray(raw.slots) ? raw.slots : [])
    .filter(isObject)
    .map((slot) => {
      const source = SLOT_SOURCES.includes(slot.source as SetupSlotSource)
        ? (slot.source as SetupSlotSource)
        : 'missing'
      return {
        field: text(slot.field),
        value: source === 'missing' ? null : (slot.value ?? null),
        source,
      }
    })
    .filter((slot) => slot.field)

  return { askingField, question: text(raw.question), multi: raw.multi === true, options, slots }
}

export function normalizeSetupCard(raw: unknown): SetupCard | null {
  if (!isObject(raw)) return null
  const draftRef = text(raw.draft_ref)
  if (!draftRef || !Array.isArray(raw.rows)) return null

  const rows = raw.rows
    .filter(isObject)
    .map((row) => ({ key: text(row.key), fields: isObject(row.fields) ? row.fields : {} }))
    .filter((row) => row.key)

  return {
    draftRef,
    capabilityId: text(raw.capability_id),
    action: raw.action === 'update' ? 'update' : 'create',
    target: text(raw.target) || null,
    irreversible: raw.irreversible === true,
    draftStatus: text(raw.draft_status) || null,
    formValues: isObject(raw.form_values) ? raw.form_values : null,
    rows,
    cost: isObject(raw.cost) ? raw.cost : null,
    counts: isObject(raw.counts) ? raw.counts : null,
    noDataWarnings: Array.isArray(raw.no_data_warnings) ? raw.no_data_warnings : [],
    existingAlerts: Array.isArray(raw.existing_alerts) ? raw.existing_alerts.filter(isObject) : [],
  }
}

export function normalizeFilePreview(raw: unknown): FilePreview | null {
  if (!isObject(raw) || !Array.isArray(raw.rows)) return null
  const counts = isObject(raw.counts) ? raw.counts : {}
  return {
    fileRef: text(raw.file_ref),
    layout: text(raw.layout),
    counts: {
      rowCount: count(counts.row_count),
      cellCount: count(counts.cell_count),
      createdCount: count(counts.created_count),
      needsChoiceCount: count(counts.needs_choice_count),
      excludedCount: count(counts.excluded_count),
    },
    cells: raw.rows
      .filter(isObject)
      .filter((cell) => typeof cell.row_index === 'number') as unknown as FilePreviewCell[],
    truncated: raw.truncated === true,
    noDataWarnings: Array.isArray(raw.no_data_warnings) ? raw.no_data_warnings : [],
  }
}

export const EMPTY_SETUP_PARTS: SetupParts = Object.freeze({
  setup: null,
  card: null,
  filePreview: null,
})

/**
 * The setup parts of an answer, wherever it carries them: `response` (main chat, live),
 * `metadata` (main chat, history), or the answer itself (feature-chat panel).
 */
export function extractSetupParts(message: unknown): SetupParts {
  const holders = isObject(message)
    ? [message.response, message.metadata, message].filter(isObject)
    : []
  let setup: SetupStep | null = null
  let card: SetupCard | null = null
  let filePreview: FilePreview | null = null
  for (const holder of holders) {
    setup = setup ?? normalizeSetupStep(holder.setup)
    card = card ?? normalizeSetupCard(holder.card)
    filePreview = filePreview ?? normalizeFilePreview(holder.file_preview)
  }
  return { setup, card, filePreview }
}

export function hasSetupParts(parts: SetupParts): boolean {
  return Boolean(parts.setup || parts.card || parts.filePreview)
}

/**
 * The text a pick sends back as the next message. The backend reads it like a typed answer
 * (`setup_memory.py`), so it is the option's words, not its value: a bare `value` or `percentile`
 * (ORP-27442's threshold type) means nothing to the model.
 */
export function setupAnswerText(options: SetupOption[]): string {
  return options
    .map((option) => (option.label || option.value).trim())
    .filter(Boolean)
    .join(', ')
}
